// src/pages/WikiPage.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, addDoc, onSnapshot, doc, deleteDoc, updateDoc, getDocs, orderBy, limit } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';
import TiptapEditor from '../components/tiptap/TiptapEditor';

// --- PÁGINA DA WIKI ---
export default function WikiPage({ userId, permissionLevel }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const itemsCollectionPath = useMemo(() => `/artifacts/${appId}/public/data/wiki-items`, []);

    useEffect(() => {
        const q = query(collection(db, itemsCollectionPath), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setItems(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [itemsCollectionPath]);

    if (selectedItem) {
        return <WikiItemDetailPage item={selectedItem} onBack={() => setSelectedItem(null)} permissionLevel={permissionLevel} />
    }

    return <WikiItemListPage items={items} loading={loading} onSelectItem={setSelectedItem} collectionPath={itemsCollectionPath} permissionLevel={permissionLevel} />;
}

// --- Lista de Itens da Wiki ---
function WikiItemListPage({ items, loading, onSelectItem, collectionPath, permissionLevel }) {
    const [newItemName, setNewItemName] = useState('');
    const [message, setMessage] = useState('');
    const [editingItemId, setEditingItemId] = useState(null);
    const [editingItemName, setEditingItemName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    const filteredItems = useMemo(() => items.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())), [items, searchTerm]);

    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!newItemName.trim()) return;
        await addDoc(collection(db, collectionPath), { name: newItemName, createdAt: new Date() });
        setMessage(`Item "${newItemName}" adicionado com sucesso!`);
        setNewItemName('');
        setTimeout(() => setMessage(''), 3000);
    };
    const handleUpdateItem = async (e) => {
        e.preventDefault();
        if(!editingItemName.trim()) return;
        await updateDoc(doc(db, collectionPath, editingItemId), { name: editingItemName });
        setEditingItemId(null);
    };
    const handleDeleteItem = async (itemId, itemName) => {
        const articlesPath = `${collectionPath}/${itemId}/articles`;
        const articlesQuery = query(collection(db, articlesPath), limit(1));
        const snapshot = await getDocs(articlesQuery);
        if (!snapshot.empty) {
            showConfirmation({ title: "Ação Bloqueada", message: `Não é possível apagar o item "${itemName}" porque ele contém artigos. Por favor, apague os artigos primeiro.`, confirmText: "OK", confirmColor: "bg-cyan-600", showCancelButton: false });
            return;
        }
        showConfirmation({
            title: "Excluir Item?", message: `Tem a certeza que deseja apagar o item "${itemName}"? Esta ação é irreversível.`, confirmText: "Excluir", confirmColor: "bg-red-600", showCancelButton: true,
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, collectionPath, itemId));
                } catch (error) {
                    showConfirmation({ title: "Erro", message: "Ocorreu um erro ao apagar o item.", confirmText: "OK", confirmColor: "bg-cyan-600", showCancelButton: false });
                }
            },
        });
    };

    return ( 
        <div className="pt-8 md:pt-12 px-4 sm:px-6 md:px-8 pb-8"> 
            <ConfirmationModal /> 
            <header className="mb-8 flex flex-col md:flex-row justify-between items-start gap-4"> 
                <div> 
                    <h1 className="text-4xl sm:text-5xl font-bold text-cyan-400">Wiki</h1> 
                    <p className="text-slate-400 mt-2">Selecione um item para ver os artigos ou adicione um novo.</p> 
                </div> 
                {permissionLevel === 'edit' && (
                    <div className="w-full md:w-auto md:min-w-[350px] max-w-lg bg-slate-800 p-6 rounded-xl shadow-lg shrink-0"> 
                        <h3 className="text-xl font-semibold mb-4 text-white">Adicionar Novo Item</h3> 
                        <form onSubmit={handleAddItem} className="flex gap-2"> 
                            <input type="text" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Nome do item" className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"/> 
                            <button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-4 rounded-md transition-colors shrink-0">Adicionar</button> 
                        </form> 
                        {message && <p className="text-green-400 mt-4 text-sm">{message}</p>} 
                    </div> 
                )}
            </header> 
            <div className="mb-8 relative w-full max-w-lg"> 
                <input type="text" placeholder="Pesquisar itens..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-slate-700 border border-slate-600 rounded-full py-2 pl-10 pr-4 focus:ring-2 focus:ring-cyan-400 focus:outline-none"/> 
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"> 
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg> 
                </span> 
            </div> 
            <div className="space-y-2"> 
                {loading ? <p>A carregar...</p> : filteredItems.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">Nenhum item encontrado.</p>
                ) : (
                    filteredItems.map(item => ( 
                        <div key={item.id} className="bg-slate-700 p-4 rounded-md group"> 
                            {editingItemId === item.id ? ( 
                                <form onSubmit={handleUpdateItem} className="flex gap-2 items-center"> 
                                    <input type="text" value={editingItemName} onChange={(e) => setEditingItemName(e.target.value)} className="flex-grow bg-slate-900 border border-slate-600 rounded-md p-1 focus:ring-2 focus:ring-cyan-400 focus:outline-none"/> 
                                    <button type="submit" className="p-1 text-green-400 hover:text-green-300">✓</button> 
                                    <button type="button" onClick={() => setEditingItemId(null)} className="p-1 text-red-400 hover:text-red-300">X</button> 
                                </form> 
                            ) : ( 
                                <div className="flex justify-between items-center"> 
                                    <span onClick={() => onSelectItem(item)} className="font-bold text-lg cursor-pointer hover:text-cyan-300 flex-grow">{item.name}</span> 
                                    {permissionLevel === 'edit' && (
                                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"> 
                                            <button onClick={() => { setEditingItemId(item.id); setEditingItemName(item.name); }} className="p-1 text-slate-400 hover:text-cyan-300"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg></button> 
                                            <button onClick={() => handleDeleteItem(item.id, item.name)} className="p-1 text-slate-400 hover:text-red-400"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button> 
                                        </div> 
                                    )}
                                </div> 
                            )} 
                        </div>
                    ))
                )}
            </div>
        </div> 
    );
}

function WikiItemDetailPage({ item, onBack, permissionLevel }) {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formMode, setFormMode] = useState(null); 
    const [currentItem, setCurrentItem] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    const articlesCollectionPath = useMemo(() => `/artifacts/${appId}/public/data/wiki-items/${item.id}/articles`, [item.id]);

    useEffect(() => {
        const q = query(collection(db, articlesCollectionPath), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [articlesCollectionPath]);

    const handleOpenForm = (mode, item = null) => { 
        if (permissionLevel !== 'edit') return;
        setFormMode(mode); 
        setCurrentItem(item); 
    };
    const handleCloseForm = () => { setFormMode(null); setCurrentItem(null); };
    const handleDeleteArticle = (articleId, articleName) => {
        if (permissionLevel !== 'edit') return;
        showConfirmation({
            title: "Excluir Artigo?", message: `Tem a certeza que deseja apagar o artigo "${articleName}"?`,
            onConfirm: async () => { await deleteDoc(doc(db, articlesCollectionPath, articleId)); }
        });
    };
    const toggleItemExpansion = (itemId) => { setExpandedItems(prev => ({...prev, [itemId]: !prev[itemId]})); };

    return ( 
        <div>
            <style>{`
                .prose p {
                    margin-top: 1em;
                    margin-bottom: 1em;
                }
            `}</style>
            <ConfirmationModal /> 
            {formMode && permissionLevel === 'edit' && <WikiArticleFormModal mode={formMode} item={currentItem} onClose={handleCloseForm} collectionPath={articlesCollectionPath} />} 
            <header className="mb-8"> 
                <button onClick={onBack} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-200 mb-4">&larr; Voltar para a lista de itens</button> 
                <h1 className="text-4xl sm:text-5xl font-bold text-cyan-400">{item.name}</h1> 
                <p className="text-slate-400 mt-2">Base de conhecimento para este item.</p> 
            </header> 
            {permissionLevel === 'edit' && (
                <button onClick={() => handleOpenForm('add')} className="w-full mb-8 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-4 rounded-md transition-colors text-lg"> + Adicionar Novo Artigo </button> 
            )}
            <div className="space-y-4"> 
                {loading ? <p>A carregar artigos...</p> : articles.length > 0 ? ( 
                    articles.map(article => { 
                        const isExpanded = !!expandedItems[article.id]; 
                        const cleanContent = DOMPurify.sanitize(article.content || ''); 
                        return ( 
                            <div key={article.id} className="bg-slate-800 rounded-lg shadow-md"> 
                                <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleItemExpansion(article.id)}> 
                                    <h3 className="font-bold text-xl text-cyan-400">{article.name}</h3> 
                                    <span className={`transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}> 
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg> 
                                    </span> 
                                </div> 
                                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[5000px]' : 'max-h-0'}`}> 
                                    <div className="px-4 pb-4 border-t border-slate-700"> 
                                        {permissionLevel === 'edit' && (
                                            <div className="flex justify-end items-center gap-2 pt-2 mb-2"> 
                                                <button onClick={() => handleOpenForm('edit', article)} className="text-slate-400 hover:text-cyan-300 p-1"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg></button> 
                                                <button onClick={() => handleDeleteArticle(article.id, article.name)} className="text-red-500 hover:text-red-400 p-1"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button> 
                                            </div> 
                                        )}
                                        <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: cleanContent }} /> 
                                    </div> 
                                </div> 
                            </div> 
                        ); 
                    }) 
                ) : ( 
                    <p className="text-slate-500 text-center py-8">Nenhum artigo para este item ainda. Adicione o primeiro!</p> 
                )} 
            </div> 
        </div> 
    );
}

function WikiArticleFormModal({ mode, item, onClose, collectionPath }) {
    const isEdit = mode === 'edit';
    const [name, setName] = useState(isEdit ? item.name : '');
    const [content, setContent] = useState(isEdit ? item.content : '');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isContentEmpty = !content || content.trim() === '<p></p>';
        if (!name.trim() || isContentEmpty) {
            setError("O nome e o conteúdo não podem estar vazios.");
            return;
        }
        setError('');
        const dataToSave = { name, content, updatedAt: new Date() };
        if (isEdit) {
            await updateDoc(doc(db, collectionPath, item.id), dataToSave);
        } else {
            dataToSave.createdAt = new Date();
            await addDoc(collection(db, collectionPath), dataToSave);
        }
        onClose();
    };

    return ( <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-40 p-4"> <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"> <header className="p-6 border-b border-slate-700"> <h3 className="font-bold text-2xl text-cyan-300">{isEdit ? `Editando Artigo` : 'Novo Artigo da Wiki'}</h3> </header> <div className="p-6 overflow-y-auto flex-grow flex flex-col"> {error && <p className="bg-red-800/80 text-white p-3 rounded-md mb-4 text-center">{error}</p>} <div className="space-y-4 flex-grow flex flex-col"> <div> <label htmlFor="wiki-name" className="block text-sm font-medium mb-1 text-slate-300">Nome do Artigo</label> <input id="wiki-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Título do artigo" className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 focus:ring-2 focus:ring-cyan-400 focus:outline-none"/> </div> <div className="flex-grow flex flex-col"> <label className="block text-sm font-medium mb-1 text-slate-300">Conteúdo</label> <TiptapEditor content={content} onChange={(newContent) => setContent(newContent)} /> </div> </div> </div> <footer className="p-6 border-t border-slate-700 mt-auto"> <div className="flex gap-4"> <button onClick={handleSubmit} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-md transition-colors">Salvar</button> <button type="button" onClick={onClose} className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-md transition-colors">Cancelar</button> </div> </footer> </div> </div> );
}
